import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RECORD_INVOICE_TYPE, REPRESENTATIVE_COMPANY_LIST } from "@/constants";
import { useToast } from "@/components/ui/use-toast";
import { getBatchInvoiceSession, processBatchInvoiceSession, removeInvoiceFromBatchSession } from "@/service/weexpertService";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { format } from "date-fns";
import { Loader, Trash2 } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Link, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

const SESSION_STATUS_COLORS = {
  PENDING: "bg-yellow-500",
  IN_PROGRESS: "bg-blue-500",
  SUCCEEDED: "bg-green-500",
  PARTIALLY_SUCCEEDED: "bg-orange-500",
  FAILED: "bg-red-500",
};

const ITEM_STATUS_COLORS = {
  PENDING: "bg-yellow-500",
  IN_PROGRESS: "bg-blue-500",
  SUBMITTED: "bg-green-500",
  FAILED: "bg-red-500",
};

const REPRESENTATIVE_COMPANY_LABELS = Object.fromEntries(
  REPRESENTATIVE_COMPANY_LIST.map((c) => [c.id, c.value])
);

function getInvoiceTypeBadgeClass(type) {
  switch (type) {
    case "PROFORMA":
      return "bg-blue-500";
    case "SALES_INVOICE":
      return "bg-green-500";
    case "PREPAID_INVOICE":
      return "bg-yellow-500";
    case "FAKTURA_KONCOWA":
      return "bg-purple-500";
    case "FAKTURA_KORYGUJACA":
      return "bg-orange-500";
    default:
      return "bg-red-500";
  }
}

function ItemsTable({ items, t, sessionId, sessionStatus }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const removeMutation = useMutation({
    mutationFn: (invoiceId) => removeInvoiceFromBatchSession(sessionId, invoiceId),
    onSuccess: () => {
      toast({ title: t("invoices:batch_session_remove_success") });
      queryClient.invalidateQueries(["batch-invoice-session", String(sessionId)]);
    },
    onError: (error) => {
      const message = error.response?.data?.message || t("invoices:batch_session_remove_error");
      toast({
        title: t("invoices:batch_session_remove_error"),
        description: message,
        variant: "destructive",
      });
    },
  });

  const isPending = sessionStatus === "PENDING";

  const columns = useMemo(
    () => [
      {
        accessorKey: "document",
        header: t("invoices:document"),
        cell: ({ row }) => {
          const invoice = row.original.invoice;
          if (!invoice) return null;
          return (
            <Link
              to={`/admin/invoices/details/${invoice.id}`}
              className="text-blue-600 hover:underline"
            >
              {invoice.invoiceId}
            </Link>
          );
        },
        key: uuidv4(),
      },
      {
        accessorKey: "client",
        header: t("invoices:select_client"),
        cell: ({ row }) => {
          const client = row.original.invoice?.client;
          if (!client) return null;
          return `${client.firstName} ${client.lastName}`;
        },
        key: uuidv4(),
      },
      {
        accessorKey: "invoiceType",
        header: t("invoices:invoice_type"),
        cell: ({ row }) => {
          const invoiceType = row.original.invoice?.invoiceType;
          if (!invoiceType) return null;
          return (
            <Badge className={getInvoiceTypeBadgeClass(invoiceType)}>
              {RECORD_INVOICE_TYPE[invoiceType]?.(t) ?? invoiceType}
            </Badge>
          );
        },
        key: uuidv4(),
      },
      {
        accessorKey: "status",
        header: t("invoices:batch_session_item_status"),
        cell: ({ row }) => (
          <Badge
            className={
              ITEM_STATUS_COLORS[row.original.status] ?? "bg-gray-500"
            }
          >
            {row.original.status}
          </Badge>
        ),
        key: uuidv4(),
      },
      {
        accessorKey: "errorMessage",
        header: t("invoices:batch_session_error"),
        cell: ({ row }) =>
          row.original.errorMessage ? (
            <span className="text-red-600 text-sm">
              {row.original.errorMessage}
            </span>
          ) : null,
        key: uuidv4(),
      },
      ...(isPending
        ? [
            {
              accessorKey: "actions",
              header: "",
              cell: ({ row }) => {
                const invoiceId = row.original.invoice?.id;
                if (!invoiceId) return null;
                return (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={removeMutation.isLoading}
                    onClick={() => removeMutation.mutate(invoiceId)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                );
              },
              key: uuidv4(),
            },
          ]
        : []),
    ],
    [t, isPending, removeMutation]
  );

  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead className="pt-3 pb-3" key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell className="text-sm pt-3 pb-3" key={cell.id}>
                    {flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext()
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="h-24 text-center"
              >
                {t("invoices:batch_session_no_items")}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

export default function BatchInvoiceSessionPage() {
  const { id } = useParams();
  const { t } = useTranslation();

  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data, isLoading } = useQuery({
    queryKey: ["batch-invoice-session", id],
    queryFn: () => getBatchInvoiceSession(id),
  });

  const processSessionMutation = useMutation({
    mutationFn: () => processBatchInvoiceSession(id),
    onSuccess: () => {
      toast({ title: t("invoices:batch_session_process_success") });
      queryClient.invalidateQueries(["batch-invoice-session", id]);
    },
    onError: (error) => {
      const message = error.response?.data?.message || t("invoices:batch_session_process_error");
      toast({
        title: t("invoices:batch_session_process_error"),
        description: message,
        variant: "destructive",
      });
    },
  });

  const session = data?.data;
  const allItems = session?.items ?? [];

  const itemsByCompany = useMemo(() => {
    const grouped = {};
    for (const item of allItems) {
      const company = item.invoice?.representativeCompany;
      if (!company) continue;
      if (!grouped[company]) grouped[company] = [];
      grouped[company].push(item);
    }
    return grouped;
  }, [allItems]);

  const companyKeys = Object.keys(itemsByCompany);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-16">
        <Loader className="animate-spin h-8 w-8" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="p-8">
        <p className="text-gray-600">{t("invoices:batch_session_no_items")}</p>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between pb-4">
        <h1 className="text-2xl md:text-4xl">
          {t("invoices:batch_session_title")}
        </h1>
        <div className="flex items-center gap-2">
          {session.status === "PENDING" && (
            <Button
              disabled={processSessionMutation.isLoading}
              onClick={() => processSessionMutation.mutate()}
              className="text-blueText bg-blueLightAccent hover:bg-blueLightAccent border-[1px] border-blueBorder shadow-none"
            >
              {processSessionMutation.isLoading
                ? t("invoices:batch_session_processing")
                : t("invoices:batch_session_process_button")}
            </Button>
          )}
          <Link to="/admin/batch-invoice-sessions">
            <Button>{t("button_back")}</Button>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pb-6">
        <div>
          <p className="text-sm text-gray-500">
            {t("invoices:batch_session_status")}
          </p>
          <Badge
            className={SESSION_STATUS_COLORS[session.status] ?? "bg-gray-500"}
          >
            {session.status}
          </Badge>
        </div>
        <div>
          <p className="text-sm text-gray-500">
            {t("invoices:batch_session_related_date")}
          </p>
          <p className="font-medium">
            {session.relatedDate
              ? format(new Date(session.relatedDate), "dd/MM/yyyy")
              : "—"}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-500">
            {t("invoices:batch_session_created_at")}
          </p>
          <p className="font-medium">
            {session.createdAt
              ? format(new Date(session.createdAt), "dd/MM/yyyy HH:mm")
              : "—"}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-500">
            {t("invoices:batch_session_created_by")}
          </p>
          <p className="font-medium">
            {session.createdBy
              ? `${session.createdBy.firstName} ${session.createdBy.lastName}`
              : "—"}
          </p>
        </div>
      </div>

      {session.errorMessage && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {session.errorMessage}
        </div>
      )}

      <h2 className="text-lg font-semibold pb-2">
        {t("invoices:batch_session_items")}
      </h2>

      <Tabs defaultValue="all" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="all" className="min-w-[100px]">
            {t("invoices:batch_session_tab_all")} ({allItems.length})
          </TabsTrigger>
          {companyKeys.map((company) => (
            <TabsTrigger
              key={company}
              value={company}
              className="min-w-[100px]"
            >
              {REPRESENTATIVE_COMPANY_LABELS[company] ?? company}{" "}
              ({itemsByCompany[company].length})
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="all">
          <ItemsTable items={allItems} t={t} sessionId={id} sessionStatus={session.status} />
        </TabsContent>

        {companyKeys.map((company) => (
          <TabsContent key={company} value={company}>
            <ItemsTable items={itemsByCompany[company]} t={t} sessionId={id} sessionStatus={session.status} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
